import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { FiUserPlus } from "react-icons/fi";

import { Avatar } from "MVVM/Components/AvatarV2";
import { FollowButton } from "MVVM/Components/Button";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";
import { TMutateFollowers } from "MVVM/Hooks/@types";
import { useUser } from "MVVM/Hooks/useUser";
import { AllstarModalState } from "MVVM/State/modals";
import { SuggestedFollowing } from "MVVM/State/suggestedFollowing";
import { ModalType } from "MVVM/@types";
import { SuggestedFeedViewModel } from "../../../SuggestedFeed/ViewModel";
import { SUGGESTED_REASON_CONFIG } from "../../../SuggestedItem/reasonConfig";
import { ISuggestedFollow } from "../../../@types";
import { trackFeedClick } from "../../analytics";
import { WidgetCard, WidgetHeader, WidgetTitle } from "../styledComponents";
import {
  AvatarButton,
  FollowButtonSlot,
  List,
  NameLine,
  Row,
  RowText,
  SubLine,
} from "./styledComponents";

const MAX_SUGGESTIONS = 4;

export const WhoToFollowWidget = () => {
  const { allstarUser } = useUser();
  const history = useHistory();
  const { suggestedFollowers, loading, error } = SuggestedFeedViewModel();
  const [suggestedFollowing, setSuggestedFollowing] =
    useRecoilState(SuggestedFollowing);
  const { mutateFollowers } = useMutateFollowers();
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);

  if (loading || error || !suggestedFollowers?.length) return null;

  const followUnfollow = (data: TMutateFollowers) => {
    setSuggestedFollowing({
      ids: [...suggestedFollowing.ids, data?.variables?.userToFollow],
    });
    return mutateFollowers(data);
  };

  const setFollowModal = ({
    displayName,
    avatar,
  }: {
    displayName: string;
    avatar: string;
  }) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: { target: displayName, avatar, action: "follow" },
    });
  };

  const goToProfile = (username: string, userId: string) => {
    if (!username) return;
    trackFeedClick("profile", { username });
    history.push(
      username === "User" ? `/profile?user=${userId}` : `/u/${username}`,
    );
  };

  const entries = suggestedFollowers.slice(0, MAX_SUGGESTIONS);

  return (
    <WidgetCard>
      <WidgetHeader>
        <WidgetTitle>
          <FiUserPlus size={15} />
          Who to follow
        </WidgetTitle>
      </WidgetHeader>

      <List>
        {entries.map(({ user, reason }: ISuggestedFollow) => {
          const username =
            user.username !== "User"
              ? user.username
              : user?.steam?.personaname ?? "User";
          const reasonConfig =
            SUGGESTED_REASON_CONFIG[reason] ||
            SUGGESTED_REASON_CONFIG["Unspecified"];

          return (
            <Row key={user._id}>
              <AvatarButton onClick={() => goToProfile(username, user._id)}>
                <Avatar
                  size="sm"
                  src={user.avatar}
                  userGroup={user.userGroup?.type || "FREE"}
                  rank={user.rank}
                  profileCardUsername={username}
                />
              </AvatarButton>
              <RowText onClick={() => goToProfile(username, user._id)}>
                <NameLine>{username}</NameLine>
                <SubLine>{reasonConfig.display}</SubLine>
              </RowText>
              <FollowButtonSlot>
                <FollowButton
                  size="tiny"
                  followUnfollow={followUnfollow}
                  setFollowModal={setFollowModal}
                  userId={user._id}
                  allstarUser={allstarUser}
                  displayName={username}
                  avatar={user.avatar}
                  trackingInfo={{ avatarPresent: !!user.avatar, reason }}
                />
              </FollowButtonSlot>
            </Row>
          );
        })}
      </List>
    </WidgetCard>
  );
};
